import React, {useCallback, useEffect, useState} from 'react';
import clsx from 'clsx';
import {formControlClasses} from '@mui/material/FormControl';
import {inputBaseClasses} from '@mui/material/InputBase';
import {inputClasses} from '@mui/material/Input';
import {outlinedInputClasses} from '@mui/material/OutlinedInput';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {
	SearchFilter,
	searchFilterClasses,
	searchFilterEndAdornmentClasses,
	type SearchFilterProps,
	searchFilterStartAdornmentClasses,
} from '../SearchFilter';
import {
	getSearchFilterExpandableUtilityClass,
	searchFilterExpandableClasses,
	type SearchFilterExpandableClasses,
} from './searchFilterExpandableClasses';

export const COLLAPSED_WIDTH = 48;
export const DEFAULT_EXPANDED_WIDTH = 200;
export const MINIMUM_EXPANDED_WIDTH = 75;

type SearchFilterExpandableOwnerState = WithClasses<
	SearchFilterExpandableClasses,
	{
		expanded: boolean;
		width?: number | string;
	}
>;

const useUtilityClasses = (ownerState: SearchFilterExpandableOwnerState) => {
	const {classes, expanded} = ownerState;
	const slots = {
		root: ['root', expanded && 'expanded'],
	};
	return composeClasses(slots, getSearchFilterExpandableUtilityClass, classes);
};

const SearchFilterExpandableFieldRoot = styled('div', {
	name: 'SearchFilterExpandable',
	slot: 'Root',
	shouldForwardProp: (prop) => prop !== 'ownerState',
})(({theme, ownerState}: WithOwnerState<SearchFilterExpandableOwnerState>) => {
	const {expanded, width} = ownerState || {};
	return {
		[`& .${searchFilterClasses.root}`]: {
			flex: '0 0 auto',
			width: COLLAPSED_WIDTH,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
			[`& .${inputClasses.root}:not(.${outlinedInputClasses.root})`]: {
				[`& .${searchFilterStartAdornmentClasses.root}`]: {
					transform: `translateX(13px)`,
				},
			},
			[`& .${formControlClasses.root}`]: {
				width: '100%',
			},
		},
		[`&:not(.${searchFilterExpandableClasses.expanded})`]: {
			[`& .${searchFilterEndAdornmentClasses.root}`]: {
				visibility: 'hidden',
			},
			[`& .${inputBaseClasses.input}`]: {
				padding: `0 8px`,
			},
		},
		[`&.${searchFilterExpandableClasses.expanded}`]: {
			[`& .${searchFilterClasses.root}`]: {
				width:
					typeof width === 'string' && width.includes('%')
						? width
						: Math.max(
								parseInt((width ?? '0').toString()) || DEFAULT_EXPANDED_WIDTH,
								MINIMUM_EXPANDED_WIDTH,
							),
			},
			[`& .${inputClasses.root}:not(.${outlinedInputClasses.root})`]: {
				[`& .${searchFilterStartAdornmentClasses.root}`]: {
					transform: `translateX(0)`,
				},
			},
		},
	};
});

export interface SearchFilterExpandableProps
	extends
		Omit<SearchFilterProps, 'classes' | 'sx'>,
		WithClasses<SearchFilterExpandableClasses> {
	expanded?: boolean;
	width?: number | string;
	onOpen?: () => void;
	onClose?: () => void;
}

export const SearchFilterExpandablePrimitive = ({
	classes: _classes,
	className,
	expanded: _expanded,
	width,
	onClose,
	onOpen,
	...props
}: SearchFilterExpandableProps) => {
	const [focused, setFocused] = useState(false);

	const filtered = props.filterStr != null && props.filterStr != '';
	const expanded = _expanded || filtered || focused || props.focused === true;

	const ownerState: SearchFilterExpandableOwnerState = {
		classes: _classes,
		className,
		expanded,
		width,
	};

	const classes = useUtilityClasses(ownerState);

	const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
		if (event.currentTarget.contains(event.relatedTarget as Node)) return;
		setFocused(false);
	}, []);

	const handleFocus = useCallback(() => setFocused(true), []);

	const notifyCollapseChange = useConstCallback(() => {
		expanded ? onOpen?.() : onClose?.();
	});

	useEffect(() => {
		notifyCollapseChange();
	}, [expanded]);

	return (
		<SearchFilterExpandableFieldRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<SearchFilter {...props} />
		</SearchFilterExpandableFieldRoot>
	);
};

export const SearchFilterExpandable = SearchFilterExpandablePrimitive;
export default SearchFilterExpandable;
